// import extractSizesAssociatedToColor from '../../components/add-to-cart-alt/extractSizesAssociatedToColor';
import { filtered_products } from './filteredCollection';
import { getSizedImageURL } from '../../core/images';
import { TILE_SIZES } from './gridLayout';
import getProductURL from '../../core/productURL';
import loadCombinedListingParents from '../../core/storefrontAPI/load-combined-listing-parents';
import { priceMarkup, promoBlocksForTile, flagsForTile } from './productTileTemplateNosto';

// Price, promo and flag HTML rendered for the tile's own color, restored when the shopper picks it again
const original_tile_details = new WeakMap();

const getProductJson = (tile) => {
  const is_search = document.body.classList.contains('has-overlay-search');
  let product_json = null;

  if (is_search) {
    product_json = window.theme.search.products;
  } else if (document.body.classList.contains('template-plp')) {
    // Can have multiple same product id on sliced products
    product_json = Array.from(filtered_products).filter(
      product => String(product.product_id) === String(tile.dataset.productId)
    );
  } else if (document.body.className.includes('lookbook')) {
    product_json = window.theme.lookbook_products.find(
      product => product.handle === tile.dataset.productHandle
    );
  } else {
    const product_list = window.theme.collections.data.find(
      collection => collection.handle === 'product-list'
    );
    if (product_list && Array.isArray(product_list.products)) {
      product_json = product_list.products.filter(
        product => product.id === Number(tile.dataset.productId)
      );
    }
  }

  return product_json;
};

// Update the flag if there's a variant metafield flag is available, else we restore the default flag
// Priority: Best Seller app flag > Trending app flag > Style metafield flag > Tag-based flag
// The style flag (variant metafield) replaces the tile's default flag (tag or bestseller) when defined
const applyCustomFlag = (tile, flag_custom_value) => {
  const flags_container = tile.querySelector('.product-tile__flags');
  // Query all matches (not just the first) so any stray/duplicate flag left over from a
  // previous selection is always cleaned up instead of silently staying visible.
  const default_flags = Array.from(flags_container.querySelectorAll('.product-tile__flag--tag, .product-tile__flag--app'));
  const custom_flags = Array.from(flags_container.querySelectorAll('.product-tile__flag--custom'));

  // Can be different by style - will need to be re-created
  custom_flags.forEach((flag) => flag.remove());

  if (flag_custom_value === '') {
    // Show the default flag(s)
    default_flags.forEach((flag) => { flag.classList.remove('visually-hidden'); flag.removeAttribute('aria-hidden'); });
  } else {
    // Create and show the custom tag if no app flag / Hide the tag-based flag.
    // .visually-hidden alone isn't enough here: it's a sr-only clip technique,
    // so the hidden flag's text stays exposed to assistive tech and gets
    // stacked together with the newly appended custom flag's text in the
    // accessible name computation. aria-hidden fully excludes it.
    default_flags.forEach((flag) => { flag.classList.add('visually-hidden'); flag.setAttribute('aria-hidden', 'true'); });

    const custom_flag_element = document.createElement('div');
    custom_flag_element.classList.add('product-tile__flag');
    custom_flag_element.classList.add('product-tile__flag--custom');
    custom_flag_element.innerHTML = flag_custom_value;
    flags_container.appendChild(custom_flag_element);
  }
};

const updateTileFlag = (product_json, swatch) => {
  const tile = swatch.closest('.product-tile');

  if (!tile.querySelector('.product-tile__flags')) {
    return;
  }

  const color_value = swatch.dataset.colorValue;
  // const p_json = (Array.isArray(product_json)) ? _.find(product_json, { skus: [{ color_value }] }) : product_json;
  const p_json = Array.isArray(product_json)
    ? product_json.flatMap(product => product.skus.filter(sku => sku.custom_fields?.Color === color_value))
    : Array.isArray(product_json?.skus)
      ? product_json.skus.filter(sku => sku.custom_fields?.Color === color_value)
      : [];

  if (typeof p_json === 'undefined' || p_json === null) {
    return;
  }

  const is_plus = color_value.includes('Plus Size -');

  // We don't have the value on a single size anymore
  // Find the 1st value of the metafield tag if any
  const flag_standard_size_value = p_json.find(sku => sku.custom_fields?.['astr-style_flag'])?.custom_fields?.['astr-style_flag'] ?? '';
  const flag_plus_size_value = p_json.find(sku => sku.custom_fields?.['astr-style_flag_plus_sizes'])?.custom_fields?.['astr-style_flag_plus_sizes'] ?? '';
  const flag_custom_value = (flag_standard_size_value === '' && flag_plus_size_value === '') ? '' : (is_plus) ? flag_plus_size_value : flag_standard_size_value;

  applyCustomFlag(tile, flag_custom_value);
};

// Combined listing children are one product per color, so their title carries the color as a suffix
// ("Scottie Striped Knit Polo Sweater - Blue stripe") while the swatch row lists the whole family.
// Rewrite that suffix so the title matches the color the shopper just picked.
const syncCombinedListingTitle = (tile, swatch) => {
  // The color isn't part of the title when disabled in theme settings (see titleForTile())
  if (!tile.dataset.parentId || window.theme.productTiles?.combined_listings_show_color_title === false) {
    return;
  }

  const title_element = tile.querySelector('[data-product-tile-title] a span') || tile.querySelector('[data-product-tile-title] a');
  const selected_color = (swatch.dataset.colorValue || '').trim();

  if (!title_element || selected_color === '') {
    return;
  }

  const current_title = (title_element.textContent || '').trim();
  const title_parts = current_title.split(' - ');

  if (title_parts.length < 2) {
    return;
  }

  // Only swap the last segment when it really is a color from this tile's swatch row, so a product
  // whose name legitimately contains " - " never gets a color appended to its full title.
  const swatch_colors = Array.from(tile.querySelectorAll('[data-color-swatch]'))
    .map((item) => (item.dataset.colorValue || '').trim().toLowerCase());

  if (!swatch_colors.includes(title_parts[title_parts.length - 1].trim().toLowerCase())) {
    return;
  }

  title_element.textContent = `${title_parts.slice(0, -1).join(' - ')} - ${selected_color}`;
};

const setTileLinks = (tile, url) => {
  const image_link = tile.querySelector('[data-product-tile-image]');
  if (image_link) {
    image_link.setAttribute('href', url);
  }

  const title_link = tile.querySelector('[data-product-tile-title] a');
  if (title_link) {
    title_link.setAttribute('href', url);
  }

  const rating_link = tile.querySelector('[data-product-tile-rating]');
  if (rating_link) {
    rating_link.setAttribute('href', url + '#pdp-reviews-anchor');
  }
};

const syncTileImageAriaLabel = (tile) => {
  const imageLink = tile.querySelector('[data-product-tile-image]');
  if (!imageLink) {
    return;
  }

  const titleElement = tile.querySelector('[data-product-tile-title] a span, [data-product-tile-title] a');
  const titleText = (titleElement?.textContent || '').trim();
  const fallbackLabel = (imageLink.getAttribute('aria-label') || '').trim();
  const baseLabel = titleText || fallbackLabel;

  if (!baseLabel) {
    return;
  }

  const visibleFlag = Array.from(tile.querySelectorAll('.product-tile__flags .product-tile__flag'))
    .find((flag) => !flag.classList.contains('visually-hidden') && flag.getAttribute('aria-hidden') !== 'true' && flag.textContent && flag.textContent.trim() !== '');
  const flagText = visibleFlag ? visibleFlag.textContent.trim() : '';

  imageLink.setAttribute('aria-label', flagText ? `${baseLabel} ${flagText}` : baseLabel);
};

// Promo messages rendered by promoBlocksForTile(), between the price and the swatches
const PROMO_SELECTOR = '[data-product-tile-featured-message], [data-product-tile-price-promo], [data-product-tile-message]';

const setTileColorDetails = (tile, price_html, promos_html, flags_html) => {
  const price_element = tile.querySelector('[data-product-tile-price]');
  const flags_element = tile.querySelector('.product-tile__flags');

  if (!original_tile_details.has(tile)) {
    original_tile_details.set(tile, {
      price_html: price_element?.innerHTML,
      promos_html: Array.from(tile.querySelectorAll(PROMO_SELECTOR)).map((element) => element.outerHTML).join(''),
      flags_html: flags_element?.outerHTML,
    });
  }

  if (price_element) {
    price_element.innerHTML = price_html;
    tile.querySelectorAll(PROMO_SELECTOR).forEach((element) => element.remove());
    price_element.insertAdjacentHTML('afterend', promos_html);
  }

  // Simplified tiles don't have flags
  if (flags_element && flags_html) {
    flags_element.outerHTML = flags_html;
  }
};

const restoreOriginalColorDetails = (tile) => {
  const original = original_tile_details.get(tile);
  if (original) {
    setTileColorDetails(tile, original.price_html, original.promos_html, original.flags_html);
    syncTileImageAriaLabel(tile);
  }
};

const setColorDetails = (tile, color_data) => {
  const from_price = (color_data.max_price > color_data.min_price) ? String(color_data.min_price) : '';
  const price_html = priceMarkup(color_data.min_price, color_data.compare_at_price, from_price);

  // Each color can have its own promo tags. Prices are already in the market currency.
  const promos_html = (tile.dataset.hasPromos === 'true') ? promoBlocksForTile({
    price: color_data.min_price,
    is_market_price: true,
    custom_fields: { 'astr-promo_tags': color_data.promo_tags },
  }, {}) : '';

  // Same keys as the ones read by getPrimaryFlagTextForTile()
  const flags_html = flagsForTile({
    custom_fields: {
      bestseller_style_30: color_data.bestseller_30,
      bestseller_style_14: color_data.bestseller_14,
    },
  }, color_data.tags);

  setTileColorDetails(tile, price_html, promos_html, flags_html);

  if (tile.querySelector('.product-tile__flags')) {
    applyCustomFlag(tile, color_data.style_flag);
  }

  syncTileImageAriaLabel(tile);
};

// Combined listing children always link to their parent (the child PDP only redirects there), with the selected color.
// Each color is its own product and can have its own price, promos and flag, which Nosto only gives us for the tile's
// own color, so the other colors' come from the parent too. See load-combined-listing-parents.js
const syncCombinedListingParent = async (tile, swatch) => {
  if (!tile.dataset.parentId) {
    return;
  }

  const color_value = (swatch.dataset.colorValue || '').trim();
  const selected_color = color_value.toLowerCase();
  const is_own_color = swatch.dataset.isRecommended === 'true';
  tile.dataset.selectedColor = selected_color;

  // Nosto price, promos and flag of the tile's own color, until the parent data is loaded or if it fails
  if (is_own_color) {
    restoreOriginalColorDetails(tile);
  }

  let parent = null;
  try {
    parent = await loadCombinedListingParents(tile.dataset.parentId);
  } catch (error) {
    // The links keep pointing to the child, which redirects to the parent
    console.error('[ASTR] Combined listing parent error', error);
    return;
  }

  // The shopper already picked another color
  if (tile.dataset.selectedColor !== selected_color) {
    return;
  }

  const color_data = parent.colors.get(selected_color);
  // Color only, same as the other tiles (see selectSwatch())
  setTileLinks(tile, `${getProductURL(parent.handle)}?color=${encodeURIComponent(color_value)}`);

  // The tile's own color too: outside the US, Nosto's USD price converted with Shopify.currency.rate can differ from the
  // market price (price adjustments, rounding), and every color must come from the same source
  if (color_data) {
    setColorDetails(tile, color_data);
  }
};

const selectSwatch = (item) => {
  if (item.classList.contains('active')) {
    return;
  }

  const tile = item.closest('.product-tile');
  const tile_image = tile.querySelector('[data-product-tile-image]');
  const selected_first_variant_id = parseInt(item.dataset.variantId, 10);
  // const is_search = document.querySelector('body').classList.contains('has-overlay-search');
  const tile_id = tile.id;
  const tile_pos = (tile_id) ? Number(tile_id.replace('product-tile-', '')) : null;

  Array.from(tile.querySelectorAll('[data-color-swatch]')).forEach((element) => {
    element.classList.remove('active');
    element.setAttribute('aria-pressed', 'false');
  });
  item.classList.add('active');
  item.setAttribute('aria-pressed', 'true');

  // Reset the horizontal image slider back to the first image on mobile
  const slider = tile.querySelector('.product-tile__images-slider');
  if (slider) {
    slider.scrollLeft = 0;
  }

  let image_class = '';
  let image_wrapper = '';
  if (tile_image.querySelector('.responsive-image__wrapper') !== null) {
    image_class = 'responsive-image__image';
    image_wrapper = ' .responsive-image__wrapper';
  }

  // const placeholder = 'https://cdn.shopify.com/s/files/1/2483/6632/files/product-tile-bkg.gif?v=1611803919';
  const placeholder = window.theme.productTiles.placeholder;

  // Load the new images (sizes are defined in collection-grid-swatch.liquid)
  if (typeof item.dataset.front !== 'undefined' && item.dataset.front !== '' && !item.dataset.front.includes('<svg')) {
    // $tile_image.find(`[data-product-main-image]${image_wrapper}`).addClass('hidden-when-not-loaded').html(`<img src="${$item.data('front').replace('300x460', '1x460')}" data-src="${$item.data('front')}" alt="${$tile.find('[data-product-tile-title] a').text()} - ${$item.data('color-value')} - Front" class="lazyload blur-up ${image_class}" />`);

    let front_url = item.dataset.front;
    if (front_url && !front_url.startsWith('https://')) {
      if (front_url.startsWith('//')) {
        front_url = 'https:' + front_url;
      } else if (!front_url.startsWith('http')) {
        front_url = 'https://' + front_url.replace(/^\/+/, '');
      }
    }

    // Remove querystring
    front_url = front_url.split('?')[0];

    let front_img_src = (front_url.includes('crop=center')) ? front_url : getSizedImageURL(front_url, '600x920_crop_center');

    if (front_img_src) {
      // Don't lazy load the product images above the fold
      let loading_type = 'lazy';
      if (tile_pos !== null) {
        loading_type = (tile_pos <= 5) ? 'eager' : 'lazy';
      }
      // let front_img_src = $item.data('front');

      // const front_img_placeholder_src = getSizedImageURL($item.data('front'), '1x_crop_center');
      // const front_img_placeholder_src = 'https://cdn.shopify.com/s/files/1/2483/6632/files/pixel-grey.png?v=1687192560';
      // front_img_src = progressiveJpg($item.data('front'));
      // https://cdn.shopify.com/s/files/1/2483/6632/products/ADR101502_SILVERFLORAL_1_300x460_crop_center@2x.jpg?v=1678350907
      // let img_dimensions = `width="300" height="460"`;
      // if (window.theme.breakpoints.current === 'mobile') {
      //   front_img_src = front_img_src.replace('300x460', '150x230');
      //   img_dimensions = `width="150" height="230"`;
      // }

      // We need the original src for comparison purpose (find the video associated to the image)
      // tile_image.querySelector(`[data-product-main-image]${image_wrapper}`).innerHTML = `<img loading="lazy" src="${front_img_src}" alt="${item.dataset.frontAlt}" class="${image_class}" ${img_dimensions} data-original-src="${item.dataset.front}" onError="this.src = '${placeholder}';" />`;

      // IMPORTANT: Don't forget to update the image within productTileTemplateNosto.js

      if (window.theme.breakpoints.current === 'mobile') {
        front_img_src = getSizedImageURL(front_url, '180x276_crop_center');
        // img_dimensions = `width="180" height="276"`;

        tile_image.querySelector(`[data-product-main-image]${image_wrapper}`).innerHTML = `
          <img
            loading="${loading_type}"
            src="${front_img_src}"
            srcset="
              ${getSizedImageURL(front_url, '90x138_crop_center')} 90w,
              ${getSizedImageURL(front_url, '180x276_crop_center')} 180w,
              ${getSizedImageURL(front_url, '250x383_crop_center')} 250w,
              ${getSizedImageURL(front_url, '400x613_crop_center')} 400w,
              ${getSizedImageURL(front_url, '450x690_crop_center')} 450w,
              ${getSizedImageURL(front_url, '500x767_crop_center')} 500w,
              ${getSizedImageURL(front_url, '700x1073_crop_center')} 700w,
              ${getSizedImageURL(front_url, '800x1200_crop_center')} 800w
            "
            sizes="${TILE_SIZES}"
            alt="${item.dataset.frontAlt}"
            class="${image_class}"
            width="180" height="276"
            data-original-src="${front_url}"
            onError="this.onerror=null; this.src='${placeholder}'; this.srcset='';"
          />
        `;
      } else {
        tile_image.querySelector(`[data-product-main-image]${image_wrapper}`).innerHTML = `
          <img
            loading="${loading_type}"
            src="${getSizedImageURL(front_url, '600x915_crop_center')}"
            srcset="
              ${getSizedImageURL(front_url, '400x613_crop_center')} 400w,
              ${getSizedImageURL(front_url, '500x767_crop_center')} 500w,
              ${getSizedImageURL(front_url, '600x915_crop_center')} 600w,
              ${getSizedImageURL(front_url, '800x1226_crop_center')} 800w,
              ${getSizedImageURL(front_url, '1000x1533_crop_center')} 1000w
            "
            sizes="${TILE_SIZES}"
            alt="${item.dataset.frontAlt}"
            class="${image_class}"
            width="600" height="915"
            data-original-src="${front_url}"
            data-new-color-selected="true"
            onError="this.onerror=null; this.src='${placeholder}'; this.srcset='';"
          />
        `;
      }

      // $tile_image.find(`[data-product-main-image]${image_wrapper}`).css({'background-image': `url(${$item.data('front').replace('300x460', '1x1')})`, 'background-size': '100%'});
      // $tile_image.find(`[data-product-main-image]${image_wrapper}`).css({'background-image': `url(${placeholder})`, 'background-size': '100%'});
    }
  }

  if (typeof item.dataset.back !== 'undefined' && item.dataset.back !== '' && tile_image.querySelector('[data-product-alt-image]') !== null) {
    let back_url = item.dataset.back;
    if (back_url && !back_url.startsWith('https://')) {
      if (back_url.startsWith('//')) {
        back_url = 'https:' + back_url;
      } else if (!back_url.startsWith('http')) {
        back_url = 'https://' + back_url.replace(/^\/+/, '');
      }
    }
    // Remove querystring
    back_url = back_url.split('?')[0];

    // const back_size_desktop = '300x460_crop_center@2x.progressive';
    const back_img_src = (item.dataset.back.includes('crop=center')) ? back_url : getSizedImageURL(back_url, '600x920_crop_center');
    // let back_img_src = $item.data('back');

    if (back_img_src) {
      // const back_img_placeholder_src = getSizedImageURL($item.data('back'), '20x_crop_center@2x.progressive');
      // const back_img_placeholder_src = 'https://cdn.shopify.com/s/files/1/2483/6632/files/pixel-grey.png?v=1687192560';
      // let back_img_dimensions = `width="300" height="460"`;
      // const back_img_src = progressiveJpg($item.data('back'));
      // if (window.theme.breakpoints.current === 'mobile') {
      //   back_img_src = back_img_src.replace('300x460', '150x230');
      //   back_img_dimensions = `width="150" height="230"`;
      // }

      // if (window.theme.breakpoints.current === 'mobile') {
      //   back_img_src = getSizedImageURL(back_url, '150x230_crop_center');
      //   back_img_dimensions = `width="150" height="230"`;
      // }

      // tile_image.querySelector(`[data-product-alt-image]${image_wrapper}`).innerHTML = `<img src="${back_img_src}" alt="${item.dataset.backAlt}" class="${image_class}" ${back_img_dimensions} onError="this.src = '${placeholder}';" />`;

      if (window.theme.breakpoints.current === 'mobile') {
        tile_image.querySelector(`[data-product-alt-image]${image_wrapper}`).innerHTML = `
          <img
            loading="lazy"
            src="${back_img_src}"
            srcset="
              ${getSizedImageURL(back_url, '90x138')} 90w,
              ${getSizedImageURL(back_url, '180x276')} 180w,
              ${getSizedImageURL(back_url, '250x383')} 250w,
              ${getSizedImageURL(back_url, '400x613')} 400w,
              ${getSizedImageURL(back_url, '450x690')} 450w,
              ${getSizedImageURL(back_url, '500x767')} 500w,
              ${getSizedImageURL(back_url, '700x1073')} 700w,
              ${getSizedImageURL(back_url, '800x1200')} 800w
            "
            width="180" height="276"
            alt="${item.dataset.backAlt}"
            class="${image_class}"
            onError="this.onerror=null; this.src = '${placeholder}'; this.srcset='';"
          />
        `;
      } else {
        tile_image.querySelector(`[data-product-alt-image]${image_wrapper}`).innerHTML = `
          <img
            loading="lazy"
            src="${back_img_src}"
            srcset="
              ${getSizedImageURL(back_url, '400x613_crop_center')} 400w,
              ${getSizedImageURL(back_url, '500x767_crop_center')} 500w,
              ${getSizedImageURL(back_url, '600x915_crop_center')} 600w,
              ${getSizedImageURL(back_url, '800x1226_crop_center')} 800w,
              ${getSizedImageURL(back_url, '1000x1533_crop_center')} 1000w
            "
            sizes="${TILE_SIZES}"
            alt="${item.dataset.backAlt}"
            class="${image_class}"
            width="600" height="915"
            onError="this.onerror=null; this.src = '${placeholder}'; this.srcset='';"
          />
        `;
      }
    }
  }

  // Change the product url (combined listing children link to their parent, see syncCombinedListingParent())
  // Color only: a variant id would preselect its size on the PDP and hide the "From" price
  const original_url = tile.dataset.productUrl;
  let new_url = original_url;
  if (typeof new_url !== 'undefined' && !tile.dataset.parentId) {
    new_url += new_url.includes('?') ? '&' : '?';
    new_url += `color=${item.dataset.colorValue}`;

    setTileLinks(tile, new_url);
  }

  /** Add to cart alt - START **/
  // Lookbook pages, reinit add to cart button
  // if ($tile.parent().hasClass('gallery-products-carousel__products')) {
  // $tile.find('[data-add-to-cart-alt-button]').html(`<span>${window.theme.strings.addToCart}</span>`);

  const product_json = getProductJson(tile);

  // Don't forget to update the code for the desktop version in components/add-to-catr-alt/index.js
  if (window.theme.breakpoints.current === 'mobile' || window.theme.breakpoints.current === 'tablet') {
    // Preselect the 1st size available
    window.setTimeout(() => {
      const available_sizes = tile.querySelectorAll('[data-size-button].available');
      if (available_sizes.length > 0) {
        const first_available_size = available_sizes[0];
        // $first_available_size.addClass('active'); // not sure why this doesn't work
        tile.querySelector('[data-add-to-cart-alt-button]').dataset.selectedVariantId = first_available_size.dataset.variantId;
      }
    }, 1);
  }
  // }

  /** Add to cart alt - END **/

  // Update the wishlist button; video if any
  const wishlist_event = new CustomEvent('tile-variant:change', { bubbles: true });
  wishlist_event.data = { detail: { variantId: selected_first_variant_id, tile: $(tile) } };
  document.dispatchEvent(wishlist_event);

  // Combined listing children don't have per-sku colors, see syncCombinedListingParent()
  if (!tile.dataset.parentId) {
    updateTileFlag(product_json, item);
  }
  // Must run before syncTileImageAriaLabel, which derives the image link's label from the title
  syncCombinedListingTitle(tile, item);
  syncTileImageAriaLabel(tile);
  syncCombinedListingParent(tile, item);

  tile.classList.add('init');
};

// The swatch a freshly built tile should start on: the recommended one when the template flagged it,
// otherwise the first of the row. Combined listing children make this mandatory rather than cosmetic:
// their swatch row lists the whole family, so defaulting to the first swatch would repaint every child
// with the family's first color and rewrite its title through syncCombinedListingTitle.
const getTileInitialSwatch = (tile) => {
  const swatches = tile.querySelectorAll('[data-color-swatch]');
  if (swatches.length === 0) {
    return null;
  }

  return tile.querySelector('[data-color-swatch][data-is-recommended="true"]') || swatches[0];
};

const initSelectNewColor = () => {
  // Can be initialized from sevaral parts (PLP, search overlay, recommendation lists...)
  if (window.select_new_color_init === true) {
    return;
  }

  window.select_new_color_init = true;

  $(document).on('click touchend', '[data-product-tile] [data-color-swatch]', (e) => {
    e.stopPropagation();
    e.preventDefault();

    selectSwatch(e.currentTarget);
  });

  $(document).on('keypress', '[data-product-tile] [data-color-swatch]', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      const item = e.currentTarget;
      e.preventDefault();
      item.click();
    }
  });
};

export {
  initSelectNewColor,
  selectSwatch,
  getTileInitialSwatch,
};
